import React from 'react';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
} from '../utils/settingsSectionStyles';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import {
  normalizeOfferTargetMarginPercent,
  OFFER_TARGET_MARGIN_MAX_PERCENT,
  parseOfferTargetMarginPercentInput,
} from '../../leads/utils/offerTargetMargin';
import { formatGermanNumber } from '../../../utils/parseGermanNumber';

const CHECKBOX_CLASS = 'h-4 w-4 rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30';

export const DEFAULT_INHERIT_HOURLY_RATE_FROM_CUSTOMER = true;

export function inheritHourlyRateFromCustomerFromUnknown(raw: unknown): boolean {
  const row = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const value = row.inherit_hourly_rate_from_customer;
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true') return true;
  if (value === 0 || value === '0' || value === 'false') return false;
  return DEFAULT_INHERIT_HOURLY_RATE_FROM_CUSTOMER;
}

type CalculationSettingsForm = {
  inheritFromCustomer: boolean;
  /** Eingabetext der Ziel-Marge; leer = keine Ziel-Hinweise. */
  targetMarginText: string;
};

function formatTargetMargin(value: number | null): string {
  return value == null
    ? ''
    : formatGermanNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function formFromUnknown(raw: unknown): CalculationSettingsForm {
  const row = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    inheritFromCustomer: inheritHourlyRateFromCustomerFromUnknown(raw),
    targetMarginText: formatTargetMargin(normalizeOfferTargetMarginPercent(row.offer_target_margin_percent)),
  };
}

const TARGET_MARGIN_ERROR = `Ziel-Marge muss leer sein oder zwischen 0 und ${OFFER_TARGET_MARGIN_MAX_PERCENT} % liegen.`;

export function CalculationHourlyRateSettingsSection() {
  const [form, setForm] = React.useState<CalculationSettingsForm>({
    inheritFromCustomer: DEFAULT_INHERIT_HOURLY_RATE_FROM_CUSTOMER,
    targetMarginText: '',
  });
  const [editSnapshot, setEditSnapshot] = React.useState<CalculationSettingsForm | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const editing = editSnapshot != null;

  React.useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setLoadError(null);
    fetch('/api/settings/company', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error || 'Einstellungen konnten nicht geladen werden.');
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setForm(formFromUnknown(data));
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Einstellungen konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const beginEdit = () => {
    setEditSnapshot(form);
    setError(null);
    setSaved(false);
  };

  const cancelEdit = () => {
    if (editSnapshot != null) setForm(editSnapshot);
    setEditSnapshot(null);
    setError(null);
  };

  const save = async () => {
    const targetMargin = parseOfferTargetMarginPercentInput(form.targetMarginText);
    if (!targetMargin.ok) {
      setError(TARGET_MARGIN_ERROR);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/settings/company', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inherit_hourly_rate_from_customer: form.inheritFromCustomer,
          offer_target_margin_percent: targetMargin.value,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || 'Speichern fehlgeschlagen.');
      }
      const data = await res.json();
      setForm(formFromUnknown(data));
      setEditSnapshot(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const disabled = !editing || saving || !loaded;
  const dirty =
    editing &&
    editSnapshot != null &&
    (form.inheritFromCustomer !== editSnapshot.inheritFromCustomer ||
      form.targetMarginText.trim() !== editSnapshot.targetMarginText.trim());

  return (
    <SettingsCard
      title="Kalkulation"
      description="Wenn beim Kunden ein Stundensatz hinterlegt ist, wird er in neuen Angeboten automatisch übernommen. Im Dokument kannst du den Satz weiterhin überschreiben. Mit einer Ziel-Marge zeigt die Kalkulation, wie viel bis zum Ziel fehlt."
      actions={
        <>
          <SettingsCardSavedStatus saved={saved && !editing} />
          {editing ? (
            <>
              <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={cancelEdit} disabled={saving}>
                Abbrechen
              </button>
              <button
                type="button"
                className={SETTINGS_BTN_PRIMARY}
                onClick={() => void save()}
                disabled={saving || !dirty}
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            </>
          ) : (
            <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={beginEdit} disabled={!loaded}>
              Bearbeiten
            </button>
          )}
        </>
      }
    >
      {error || loadError ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error || loadError}
        </div>
      ) : null}
      <div className="space-y-5">
        <label
          htmlFor="inherit-hourly-rate-from-customer"
          className={`flex items-center gap-2.5 ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
        >
          <input
            id="inherit-hourly-rate-from-customer"
            type="checkbox"
            className={CHECKBOX_CLASS}
            checked={form.inheritFromCustomer}
            disabled={disabled}
            onChange={(e) => setForm((prev) => ({ ...prev, inheritFromCustomer: e.target.checked }))}
          />
          <span className="text-sm text-app-text">Stundensatz vom Kunden übernehmen</span>
        </label>
        <div>
          <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`} htmlFor="offer-target-margin-percent">
            Ziel-Marge (%)
          </label>
          <div className="flex items-center gap-2">
            <div className="w-28">
              <input
                id="offer-target-margin-percent"
                type="text"
                inputMode="decimal"
                placeholder="z. B. 25"
                disabled={disabled}
                value={form.targetMarginText}
                onChange={(e) => {
                  const targetMarginText = e.target.value;
                  setForm((prev) => ({ ...prev, targetMarginText }));
                  if (error === TARGET_MARGIN_ERROR) setError(null);
                }}
                className={`${SETTINGS_INPUT_CLASS} text-right tabular-nums`}
              />
            </div>
            <span className="text-sm text-app-text-muted">%</span>
          </div>
          <p className="mt-1.5 text-xs text-app-text-subtle">
            Leer lassen: keine Ziel-Hinweise. Die Kalkulation im Angebot zeigt sonst, wie viel bis zur
            Ziel-Marge fehlt (0–{OFFER_TARGET_MARGIN_MAX_PERCENT} %).
          </p>
        </div>
      </div>
    </SettingsCard>
  );
}
